import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionCardMenu,
  useCanvasObjectActionHandlers,
  useObjectActions,
  type ObjectKind,
} from "@/object-actions";
import { useAttachCanvasTransferSubmenu } from "@/object-actions/transfer/useCanvasTransferSubmenu";
import { RESOURCE_TAG_FAVORITE, type Canvas } from "@cohesive/api-client";
import { CanvasCard, type CanvasOwner } from "@cohesive/ui-components";
import type React from "react";

export interface CanvasCardContainerProps {
  canvas: Canvas;
  isSelected: boolean;
  canShare: boolean;
  canDelete: boolean;
  owner?: CanvasOwner;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
}

const CANVAS_KIND: ObjectKind = { type: "canvas" };

/**
 * Registry-backed `CanvasCard`. Owns one `useRailRowContextMenu` controller so
 * the card's kebab and right-click open the same manifest-driven menu, and
 * resolves the canvas actions lazily (only while the menu is open).
 */
export function CanvasCardContainer({
  canvas,
  isSelected,
  canShare,
  canDelete,
  owner,
  onClick,
  onDoubleClick,
}: CanvasCardContainerProps) {
  const controller = useRailRowContextMenu();
  const isFavorite = !!canvas.tags?.includes(RESOURCE_TAG_FAVORITE);
  const handlers = useCanvasObjectActionHandlers({
    canvas,
    canShare,
    canDelete,
  });

  const { actions: baseActions } = useObjectActions({
    kind: CANVAS_KIND,
    surface: "context-menu",
    enabled: controller.open,
    state: {
      isFavorite,
      canShare,
      canDelete,
      canEdit: canvas.can_edit !== false,
    },
    handlers,
  });
  // Move to opens the nested destination picker, same as the rail row.
  const actions = useAttachCanvasTransferSubmenu(baseActions, canvas);

  return (
    <CanvasCard
      canvas={canvas}
      isSelected={isSelected}
      owner={owner}
      isHeroGenerating={Boolean(canvas.metadata?.hero_generating)}
      isFavorite={isFavorite}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={controller.onContextMenu}
      onToggleStar={() => handlers.star?.()}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={CANVAS_KIND}
          name={canvas.name || "Untitled"}
        />
      }
    />
  );
}
